import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Public Data Deletion Instructions.
 *
 * Required for the Meta / Facebook App configuration ("Data Deletion
 * Instructions URL"). Describes how a user can request deletion of the
 * data iEvntür holds about them, including data obtained via Facebook Login.
 *
 * Route: /data-deletion
 */
const DataDeletionPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="data-deletion-page">
        <div>
          <h1 className="text-3xl font-bold text-white">iEvntür Data Deletion Instructions</h1>
          <p className="text-zinc-400 text-sm mt-1">Effective 2026-06-01 · v1</p>
        </div>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">1. Your Right to Deletion</h2>
          <p>
            iEvntür respects your right to delete the personal information we
            hold about you, including any data we obtained when you signed in
            with Facebook or Google. This page explains exactly how to make
            that request and what happens afterward.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">2. How to Request Deletion</h2>
          <p>You can request deletion of your account and associated data in either of these ways:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong className="text-zinc-100">Email us:</strong> send a request from the email
              address on your account to{' '}
              <a className="text-amber-400 underline" href="mailto:support@ievntur.com" data-testid="data-deletion-email">
                support@ievntur.com
              </a>{' '}
              with the subject line <em>&quot;Data Deletion Request&quot;</em>.
            </li>
            <li>
              <strong className="text-zinc-100">In-app:</strong> if you have an active account, go to
              your account settings and use the account deletion option, or contact support from within
              the app.
            </li>
          </ul>
          <p className="text-zinc-400 text-sm">
            Please include the name and email (and, if applicable, the phone number) associated with your
            account so we can locate and verify it.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">3. Deleting Facebook-Login Data</h2>
          <p>
            If you signed in using Facebook and want to disconnect that
            connection, you can also remove iEvntür from your Facebook account:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Go to your Facebook <strong className="text-zinc-100">Settings &amp; Privacy → Settings</strong>.</li>
            <li>Select <strong className="text-zinc-100">Apps and Websites</strong>.</li>
            <li>Locate <strong className="text-zinc-100">iEvntür</strong> and choose <strong className="text-zinc-100">Remove</strong>.</li>
          </ul>
          <p>
            Removing the connection on Facebook stops future data sharing. To
            delete the data already stored with us, please also submit a
            deletion request as described in Section 2.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">4. What Gets Deleted &amp; What We Must Retain</h2>
          <p>
            When we process your request we permanently delete your profile,
            login connections, messages, and other personal data associated
            with your account. Certain records may be retained where required
            by law or for legitimate business purposes:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Financial, tax, and invoicing records (typically retained up to 7 years).</li>
            <li>Records needed to comply with legal, safety, or dispute-resolution obligations.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">5. Timeline</h2>
          <p>
            We acknowledge deletion requests within 5 business days and complete
            verified deletions within 30 days. We will confirm by email once your
            request has been fully processed.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">6. Contact</h2>
          <p>
            Questions about data deletion:{' '}
            <a href="mailto:support@ievntur.com" className="text-amber-400 underline">
              support@ievntur.com
            </a>
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">7. Related Documents</h2>
          <ul className="list-disc pl-6">
            <li><Link to="/privacy-policy" className="text-amber-400 underline" data-testid="link-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms-and-conditions" className="text-amber-400 underline" data-testid="link-terms">Terms &amp; Conditions</Link></li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default DataDeletionPage;
